import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { provideQuestionsWithAnswers } from "@jsq/compliance-components";
import { Button, Flex, Grid, ModalV2, QueryLoader, Text } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { type ComplianceCaseTypeEnum, ComplianceObjectType } from "~/graphql/supergraphEnums";
import { useGetProfileForComparisonQuery } from "~/graphql/supergraphHooks";
import {
  getInvestingProfileName,
  getProfileName,
} from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import { formatDocumentType } from "~/pages/Compliance/utils/documentUtils";
import { formatShortFormDate } from "~/utils/datetime";
import { useCurrentCaseContext } from "../context/CurrentCaseContext";
import { FieldKey } from "./constants";
import { type ComparisonRowData, getColumnDefs, gridOptions } from "./gridConfig";
import * as styles from "./styles_vanilla.css";
import { useDeleteRow } from "./useDeleteRow";
import {
  computeDocumentChangeFlags,
  formatAddress,
  formatControllers,
  formatOwners,
  formatQuestionText,
  formatRiskRating,
  getEntityType,
  isDifferent,
} from "./utils";

const annotateAmlRows = <T extends ComparisonRowData>(rows: T[]): T[] => {
  const amlRows = rows.filter((r) => r.fieldKey === FieldKey.AmlQuestion);
  if (amlRows.length > 0) {
    amlRows[0].amlQuestionCount = amlRows.length;
  }
  return rows;
};

export const ProposedChangesModal = () => {
  const history = useHistory();

  const { sourceProfile, targetProfile } = useParams<{
    sourceProfile: string;
    targetProfile: string;
  }>();
  const sourceProfileId = sourceProfile ? parseInt(sourceProfile) : 0;
  const targetProfileId = targetProfile ? parseInt(targetProfile) : 0;

  const { caseId, caseType, programId, caseBasePath, readonly, fatcaCrsEnabled } =
    useCurrentCaseContext();
  const showFatcaCrs = useIsFatcaCrsEnabledForCase(
    caseType as ComplianceCaseTypeEnum | undefined,
    fatcaCrsEnabled,
  );

  const currentProfileResult = useGetProfileForComparisonQuery({
    variables: { profileId: sourceProfileId ?? 0, programId, caseId, showFatcaCrs },
    skip: !sourceProfileId,
    fetchPolicy: "no-cache",
  });

  const proposedProfileResult = useGetProfileForComparisonQuery({
    variables: { profileId: targetProfileId ?? 0, programId, caseId, showFatcaCrs },
    skip: !targetProfileId,
    fetchPolicy: "no-cache",
  });

  const currentProfile = currentProfileResult.data?.complianceProfile;
  const proposedProfile = proposedProfileResult.data?.complianceProfile;

  const handleDocumentClick = useCallback(
    (docId: number, profileId: number) => {
      history.push(
        `${caseBasePath}/profiles/${sourceProfileId}/proposed-changes/${targetProfileId}/documents?profileId=${profileId}&documentId=${docId}`,
      );
    },
    [caseBasePath, history, sourceProfileId, targetProfileId],
  );

  const [showAmlWarningModal, setShowAmlWarningModal] = useState(false);

  const { deleteRow, isDeleting } = useDeleteRow({
    currentProfile,
    proposedProfile,
    proposedProfileId: targetProfileId ?? 0,
    caseId: caseId ?? 0,
    programId: programId ?? undefined,
  });

  const handleDeleteRow = useCallback(
    (row: ComparisonRowData) => {
      if (row.fieldKey === FieldKey.AmlQuestion) {
        setShowAmlWarningModal(true);
        return;
      }
      deleteRow(row);
    },
    [deleteRow],
  );

  const rowData = useMemo(() => {
    if (!currentProfile || !proposedProfile) return [];

    const formatValue = (value: string | null | undefined) => value || "--";
    const isIndividual =
      currentProfile.complianceObjectType === ComplianceObjectType.Individual;

    const currentQuestionsWithAnswers = provideQuestionsWithAnswers(
      (currentProfile.amlEntityTypeQuestions || []) as Parameters<
        typeof provideQuestionsWithAnswers
      >[0],
      currentProfile.amlEntityTypeId ?? null,
    );
    const proposedQuestionsWithAnswers = provideQuestionsWithAnswers(
      (proposedProfile.amlEntityTypeQuestions || []) as Parameters<
        typeof provideQuestionsWithAnswers
      >[0],
      proposedProfile.amlEntityTypeId ?? null,
    );

    // Check document differences
    const documentComparison = isDifferent.documents(
      currentProfile.documents,
      proposedProfile.documents,
    );

    const allRows: ComparisonRowData[] = [];

    const addStringRow = (
      fieldName: string,
      fieldKey: FieldKey,
      currentVal: string | null | undefined,
      proposedVal: string | null | undefined,
    ) => {
      if (isDifferent.string(currentVal, proposedVal)) {
        allRows.push({
          field: fieldName,
          fieldKey: fieldKey,
          current: formatValue(currentVal),
          proposed: formatValue(proposedVal),
        });
      }
    };

    const addDateRow = (
      fieldName: string,
      fieldKey: FieldKey,
      currentVal: string | null | undefined,
      proposedVal: string | null | undefined,
    ) => {
      if (isDifferent.date(currentVal, proposedVal)) {
        allRows.push({
          field: fieldName,
          fieldKey: fieldKey,
          current: formatShortFormDate(currentVal ?? null) || "--",
          proposed: formatShortFormDate(proposedVal ?? null) || "--",
        });
      }
    };

    if (isIndividual) {
      addStringRow(
        "Name",
        FieldKey.LegalName,
        getProfileName(currentProfile),
        getProfileName(proposedProfile),
      );
      addDateRow(
        "Date of birth",
        FieldKey.DateOfBirth,
        currentProfile.dateOfBirth,
        proposedProfile.dateOfBirth,
      );
      addStringRow(
        "EIN/TIN",
        FieldKey.TaxId,
        currentProfile.taxIdUnencrypted,
        proposedProfile.taxIdUnencrypted,
      );
      addStringRow(
        "Tax ID type",
        FieldKey.TaxIdType,
        currentProfile.taxIdType,
        proposedProfile.taxIdType,
      );

      if (isDifferent.riskRating(currentProfile.riskRating, proposedProfile.riskRating)) {
        allRows.push({
          field: "Risk rating",
          fieldKey: FieldKey.RiskRating,
          current: formatRiskRating(currentProfile.riskRating),
          proposed: formatRiskRating(proposedProfile.riskRating),
        });
      }
    } else {
      addStringRow(
        "Legal business name",
        FieldKey.LegalName,
        currentProfile.legalName,
        proposedProfile.legalName,
      );

      if (isDifferent.string(currentProfile.accountType, proposedProfile.accountType)) {
        allRows.push({
          field: "Entity type",
          fieldKey: FieldKey.AccountType,
          current: getEntityType(currentProfile.accountType),
          proposed: getEntityType(proposedProfile.accountType),
        });
      }

      addDateRow(
        "Date of formation",
        FieldKey.DateOfOrganization,
        currentProfile.dateOfOrganization,
        proposedProfile.dateOfOrganization,
      );
      addStringRow(
        "Identification number",
        FieldKey.TaxId,
        currentProfile.taxIdUnencrypted,
        proposedProfile.taxIdUnencrypted,
      );
      addStringRow(
        "Tax ID type",
        FieldKey.TaxIdType,
        currentProfile.taxIdType,
        proposedProfile.taxIdType,
      );

      for (const currentQuestion of currentQuestionsWithAnswers) {
        if (!currentQuestion.question?.trim()) continue;

        const proposedQuestion = proposedQuestionsWithAnswers.find(
          (q) => q.targetAmlEntityTypeId === currentQuestion.targetAmlEntityTypeId,
        );

        const currentAnswer = currentQuestion.answer || "--";
        const proposedAnswer = proposedQuestion?.answer || "--";

        if (currentAnswer !== proposedAnswer) {
          allRows.push({
            field: formatQuestionText(currentQuestion.question, currentProfile.legalName),
            fieldKey: FieldKey.AmlQuestion,
            current: currentAnswer,
            proposed: proposedAnswer,
            amlQuestionId: currentQuestion.targetAmlEntityTypeId,
          });
        }
      }
    }

    const currentAddress = formatAddress(currentProfile.addresses);
    const proposedAddress = formatAddress(proposedProfile.addresses);
    if (isDifferent.string(currentAddress, proposedAddress)) {
      allRows.push({
        field: "Address",
        fieldKey: FieldKey.Address,
        current: currentAddress,
        proposed: proposedAddress,
      });
    }

    if (documentComparison.isDifferent) {
      documentComparison.differentBaseTypes.forEach((baseType) => {
        const currentDocs = currentProfile.documents?.filter(
          (d) => (d.documentPurpose?.baseType || "other") === baseType,
        );
        const proposedDocs = proposedProfile.documents?.filter(
          (d) => (d.documentPurpose?.baseType || "other") === baseType,
        );

        const documentTitle = formatDocumentType(baseType);
        const changeFlags = computeDocumentChangeFlags(currentDocs, proposedDocs);

        const currentDocsData = currentDocs?.map((d) => ({
          id: d.id,
          name: d.name,
          s3Link: d.s3Link,
          documentPurpose: d.documentPurpose,
          status: d.status,
          metadata: d.metadata,
        }));
        const proposedDocsData = proposedDocs?.map((d) => ({
          id: d.id,
          name: d.name,
          s3Link: d.s3Link,
          documentPurpose: d.documentPurpose,
          status: d.status,
          metadata: d.metadata,
          hasMetaDataChange: changeFlags.get(d.id) ?? false,
        }));

        allRows.push({
          field: documentTitle,
          fieldKey: FieldKey.Document,
          current: currentDocsData && currentDocsData.length > 0 ? "" : "--",
          proposed: proposedDocsData && proposedDocsData.length > 0 ? "" : "--",
          isDocument: true,
          currentDocs: currentDocsData,
          proposedDocs: proposedDocsData,
        });
      });
    }

    if (
      isDifferent.owners(
        currentProfile.beneficialOwners,
        proposedProfile.beneficialOwners,
        currentProfile.id,
        proposedProfile.sourceProfileId ?? proposedProfile.id,
      )
    ) {
      allRows.push({
        field: isIndividual ? "Owner" : "Owners",
        fieldKey: FieldKey.Owners,
        current: formatOwners(currentProfile.beneficialOwners),
        proposed: formatOwners(proposedProfile.beneficialOwners),
      });
    }

    if (
      isDifferent.controllers(
        currentProfile.controllingPersons,
        proposedProfile.controllingPersons,
        currentProfile.id,
        proposedProfile.sourceProfileId ?? proposedProfile.id,
      )
    ) {
      allRows.push({
        field: isIndividual ? "Controller" : "Controllers",
        fieldKey: FieldKey.Controllers,
        current: formatControllers(currentProfile.controllingPersons),
        proposed: formatControllers(proposedProfile.controllingPersons),
      });
    }

    return annotateAmlRows(allRows);
  }, [currentProfile, proposedProfile]);

  const amlQuestionLabels = useMemo(
    () => rowData.filter((r) => r.fieldKey === FieldKey.AmlQuestion).map((r) => r.field),
    [rowData],
  );

  const hasDocumentMetaDataChange = useMemo(
    () => rowData.some((r) => r.isDocument && r.proposedDocs?.some((d) => d.hasMetaDataChange)),
    [rowData],
  );

  const handleConfirmAmlDelete = useCallback(async () => {
    await deleteRow({ fieldKey: FieldKey.AmlQuestion } as ComparisonRowData);
    setShowAmlWarningModal(false);
  }, [deleteRow]);

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        onDocumentClick: handleDocumentClick,
        onDeleteRow: handleDeleteRow,
        sourceProfileId,
        targetProfileId,
        readonly,
      }),
    [handleDocumentClick, handleDeleteRow, sourceProfileId, targetProfileId, readonly],
  );

  const handleCloseModal = useCallback(async () => {
    await client
      .refetchQueries({
        include: ["GetComplianceCase", "IsProfileDirty"],
      })
      .finally(() => history.push(`${caseBasePath}/profiles`));
  }, [caseBasePath, history]);

  if (!currentProfile || !proposedProfile) {
    return null;
  }

  return (
    <>
      <ModalV2
        show={true}
        onHide={handleCloseModal}
        title="Proposed changes"
        size="xl"
        contentClassName={styles.modalContent}
        headerClassName={styles.modalHeader}
      >
        <ModalV2.Body className={styles.modalBody}>
          <QueryLoader result={[currentProfileResult, proposedProfileResult]}>
            <Flex flexDirection="column">
              <Text variant="regular-md" className={styles.subtitle}>
                {getInvestingProfileName(currentProfile)}
              </Text>
              <div className={styles.tableContainer}>
                <Grid gridOptions={gridOptions} rowData={rowData} columnDefs={columnDefs} />
              </div>
              <div className={styles.footerContainer}>
                {hasDocumentMetaDataChange && (
                  <Text variant="regular-sm" className={styles.documentMetaDataFootnote}>
                    * Proposed changes pertains to document meta data changes.
                  </Text>
                )}
                <Button
                  variant="neutral"
                  onClick={handleCloseModal}
                  className={styles.closeButton}
                >
                  Close
                </Button>
              </div>
            </Flex>
          </QueryLoader>
        </ModalV2.Body>
      </ModalV2>
      {showAmlWarningModal && (
        <ModalV2
          show
          onHide={() => setShowAmlWarningModal(false)}
          title="Warning"
          isCentered
          size="small"
          data-testid="aml-delete-warning-modal"
        >
          <ModalV2.Body>
            <Text variant="regular-md">
              Removing answers to the investor questionnaire may result in removal of previously
              submitted data for {getInvestingProfileName(currentProfile)}.
            </Text>
            <ul className={styles.warningList}>
              {amlQuestionLabels.map((label) => (
                <li key={label}>
                  <Text variant="regular-md">{label}</Text>
                </li>
              ))}
            </ul>
          </ModalV2.Body>
          <ModalV2.Footer>
            <Flex justifyContent="flex-end" gap="md">
              <Button
                variant="neutral"
                onClick={() => setShowAmlWarningModal(false)}
                data-testid="aml-delete-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmAmlDelete}
                isLoading={isDeleting}
                data-testid="aml-delete-confirm"
              >
                Delete
              </Button>
            </Flex>
          </ModalV2.Footer>
        </ModalV2>
      )}
    </>
  );
};
